import React, { useEffect, useState } from "react";
import { LineChart } from "@mui/x-charts/LineChart";
import { CardSkeleton, TableSkeleton } from "../../../components/skeleton/ListSkeleton";
import { getDoctors } from "../analyticsApi";
import { bucketLabel, formatChange, formatNumber, formatPct, formatRs, changeTone } from "../analyticsUtils";
import { useAnalyticsPeriod } from "../useAnalyticsPeriod";
import { useAnalyticsFetch } from "../useAnalyticsFetch";
import PeriodPicker from "../components/PeriodPicker";
import EmptyState from "../components/EmptyState";
import { ChartCard, EmptyRow, SELECT, TD, TD_NUM, TH, TH_NUM, TR, TableCard, chartSx, seriesColors, useIsDark } from "../components/ui";

const MAX_LINES = 6;

const CHANGE_TONE = {
  good: "text-emerald-600 dark:text-emerald-400",
  bad: "text-rose-600 dark:text-rose-400",
  neutral: "text-slate-400 dark:text-slate-500",
};

export default function AnalyticsDoctors() {
  const period = useAnalyticsPeriod();
  const isDark = useIsDark();
  const [doctorId, setDoctorId] = useState("");
  const [options, setOptions] = useState<{ id: string; name: string }[]>([]);
  const { data, loading } = useAnalyticsFetch(() => getDoctors({ from: period.from, to: period.to }, doctorId), [period.from, period.to, doctorId]);

  // The dropdown lists every doctor seen in an unfiltered response; a filtered
  // response only carries one doctor, so it must not overwrite the list.
  useEffect(() => {
    if (data && !doctorId) setOptions(data.doctors.map((d) => ({ id: d.doctorId, name: d.name })));
  }, [data, doctorId]);

  const colors = seriesColors(isDark);
  const lines = (data?.series ?? []).slice(0, MAX_LINES);
  const months = lines[0]?.points.map((p) => bucketLabel(p.month)) ?? [];
  const noData = !loading && data != null && data.doctors.length === 0;

  return (
    <div className="space-y-4 text-slate-800 dark:text-slate-200">
      <PeriodPicker period={period}>
        <label className="text-xs text-slate-500 dark:text-slate-400" htmlFor="analytics-doctor-filter">
          Doctor
        </label>
        <select id="analytics-doctor-filter" className={SELECT} value={doctorId} onChange={(e) => setDoctorId(e.target.value)}>
          <option value="">All doctors</option>
          {options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
      </PeriodPicker>

      {noData ? (
        <EmptyState />
      ) : (
        <>
          <TableCard title="Per doctor" hint="Revenue from receipt allocations · keeps = revenue minus commission (dentists only) · change vs previous period">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-600">
                  <th className={TH}>Doctor</th>
                  <th className={TH_NUM}>Revenue</th>
                  <th className={TH_NUM}>Change</th>
                  <th className={TH_NUM}>Patients</th>
                  <th className={TH_NUM}>Average bill</th>
                  <th className={TH_NUM}>Keeps</th>
                  <th className={TH_NUM}>Appointments</th>
                  <th className={TH_NUM}>No-show rate</th>
                  <th className={TH_NUM}>Completion rate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-600">
                {loading || !data ? (
                  <TableSkeleton rows={5} colSpan={9} />
                ) : (
                  <>
                    <tr className="bg-slate-50 dark:bg-slate-700/40 font-semibold">
                      <td className={TD}>Clinic average</td>
                      <td className={TD_NUM}>{formatRs(data.clinicAverage.revenue)}</td>
                      <td className={TD_NUM}>—</td>
                      <td className={TD_NUM}>{formatNumber(data.clinicAverage.patients, 1)}</td>
                      <td className={TD_NUM}>—</td>
                      <td className={TD_NUM}>—</td>
                      <td className={TD_NUM}>—</td>
                      <td className={TD_NUM}>{formatPct(data.clinicAverage.noShowRate)}</td>
                      <td className={TD_NUM}>{formatPct(data.clinicAverage.completionRate)}</td>
                    </tr>
                    {data.doctors.length === 0 ? (
                      <EmptyRow colSpan={9} />
                    ) : (
                      data.doctors.map((d) => (
                        <tr key={d.doctorId} className={TR}>
                          <td className={TD}>
                            {d.name}
                            {d.isDentist === false && <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-400">staff</span>}
                          </td>
                          <td className={TD_NUM}>{formatRs(d.revenue ?? 0)}</td>
                          <td className={`${TD_NUM} ${CHANGE_TONE[changeTone(d.change.revenue, true)]}`}>{formatChange(d.change.revenue)}</td>
                          <td className={TD_NUM}>{formatNumber(d.patients ?? 0)}</td>
                          <td className={TD_NUM}>{formatRs(d.averageBill ?? null)}</td>
                          <td className={TD_NUM}>{formatRs(d.keeps ?? 0)}</td>
                          <td className={TD_NUM}>{formatNumber(d.appointments ?? 0)}</td>
                          <td className={TD_NUM}>{formatPct(d.noShowRate ?? null)}</td>
                          <td className={TD_NUM}>{formatPct(d.completionRate ?? null)}</td>
                        </tr>
                      ))
                    )}
                  </>
                )}
              </tbody>
            </table>
          </TableCard>

          {loading || !data ? (
            <div className="grid grid-cols-1 gap-4">
              <CardSkeleton cards={1} height="h-72" />
            </div>
          ) : (
            lines.length > 0 && (
              <ChartCard
                title="Revenue per doctor, last 6 months"
                hint={data.series.length > MAX_LINES ? `Top ${MAX_LINES} doctors by revenue in this period` : undefined}
              >
                <LineChart
                  height={300}
                  xAxis={[{ scaleType: "point", data: months }]}
                  yAxis={[{ valueFormatter: (v: number) => formatNumber(v) }]}
                  series={lines.map((l, i) => ({
                    id: l.doctorId,
                    label: l.name,
                    data: l.points.map((p) => p.revenue),
                    color: colors[i % colors.length],
                    valueFormatter: (v: number | null) => formatRs(v),
                  }))}
                  grid={{ horizontal: true }}
                  margin={{ left: 64, right: 12, top: 48, bottom: 32 }}
                  sx={chartSx(isDark)}
                />
              </ChartCard>
            )
          )}
        </>
      )}
    </div>
  );
}
